/**
 * Blocks that procedural extraction agent emits on the Redis stream
 * `procedural-stream:{fileId}`. The backend reads entries via XRANGE/XREAD,
 * parses the JSON `content` field, and forwards a flattened block over SSE.
 */
export type ProceduralStreamBlock = {
  timestamp: number;
} & (
  | { type: 'started' }
  | { type: 'ocr-started' }
  | { type: 'ocr-completed'; chunk_count: number }
  | { type: 'extract-started' }
  | { type: 'extract-progress'; event_count: number }
  | { type: 'extract-completed' }
  | { type: 'merge-started' }
  | { type: 'merge-progress'; decision_count: number }
  | { type: 'merge-completed' }
  | { type: 'persisted'; adds: number; updates: number; skipped: number }
  | { type: 'completed' }
  | { type: 'failed'; error: string }
  | { type: 'rejected'; error: string }
  // Transport-level keepalive emitted by the backend to defeat proxy idle/timeouts.
  | { type: 'heartbeat' }
);

export const PROCEDURAL_TERMINAL_BLOCK_TYPES: ReadonlySet<ProceduralStreamBlock['type']> = new Set([
  'completed',
  'failed',
  'rejected',
]);

export function isTerminalProceduralBlock(block: ProceduralStreamBlock): boolean {
  return PROCEDURAL_TERMINAL_BLOCK_TYPES.has(block.type);
}

/**
 * Parse a Redis stream entry's flat field array into a {@link ProceduralStreamBlock}.
 * Returns null if the entry is malformed (missing fields, unparseable JSON).
 */
export function parseProceduralStreamEntry(fields: string[]): ProceduralStreamBlock | null {
  const record: Partial<Record<string, string>> = {};
  for (let i = 0; i + 1 < fields.length; i += 2) {
    record[fields[i]] = fields[i + 1];
  }

  const type = record.type;
  const contentRaw = record.content;
  const timestampRaw = record.timestamp;

  if (type === undefined || contentRaw === undefined || timestampRaw === undefined) {
    return null;
  }

  let content: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(contentRaw);
    content =
      parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return null;
  }

  const timestamp = Number(timestampRaw);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  return { type, timestamp, ...content } as ProceduralStreamBlock;
}
